import { AxiosInstance } from "axios";
import QueryString from "qs";
import { Model } from "@iventis/domain-model/model/model";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { queryParams } from "@iventis/utilities";
import { Map } from "@iventis/domain-model/model/map";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { convertMapObjectToLocalGeoJson } from "@iventis/map-engine/src/utilities/state-helpers";
import { DataField } from "@iventis/domain-model/model/dataField";
import { Polygon } from "geojson";
import { RouteWaypoint } from "@iventis/domain-model/model/routeWaypoint";
import { aggregatePagedResponse } from "@iventis/api-helpers";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import { FilterFormat } from "@iventis/types";
import { LocalGeoJson } from "@iventis/map-types";

export const getMap = async (
    mapId: string,
    projectId: string,
    mappingApi: AxiosInstance,
    getWaypoints: (objectId: string, mapId: string) => Promise<RouteWaypoint[]>,
    signal?: AbortSignal
): Promise<{ map: Map; localObjects: LocalGeoJson; projectDataFields: DataField[]; mapObjectBounds: Polygon }> => {
    // Fetch all pages of map data and combine layers
    const allLayers: MapLayer[] = [];
    let pageNumber = 1;
    let hasMorePages = true;
    let firstPageMap: Map | null = null;

    while (hasMorePages) {
        const pageResponse = await mappingApi
            .get<Map>(`/maps/${mapId}`, { params: { pageNumber }, signal })
            .catch((e) => {
                throw e;
            });

        const pageMap = pageResponse.data;

        // Store the first page's complete map data
        if (pageNumber === 1) {
            firstPageMap = pageMap;
        }

        if (pageMap.layers && pageMap.layers.length > 0) {
            allLayers.push(...pageMap.layers);

            // If we got exactly 200 layers, there might be more pages
            if (pageMap.layers.length === 200) {
                pageNumber++;
            } else {
                hasMorePages = false;
            }
        } else {
            hasMorePages = false;
        }
    }

    // Ensure we have a valid first page map
    if (!firstPageMap) {
        throw new Error("Failed to fetch map data");
    }

    // Combine all layers with the first page's map data
    const map = { ...firstPageMap, layers: allLayers };

    const localOnlyObjectsRequest = getLocalOnlyGeoJson(mappingApi, mapId, [], getWaypoints, signal);

    const projectDataFieldsRequest = mappingApi.get<DataField[]>(`/projects/${projectId}/data-fields`);

    const boundsRequest = getMapObjectBounds(mappingApi, mapId, queryParams.level);

    const [localObjects, { data: projectDataFields }, { data: mapObjectBounds }] = await Promise.all([localOnlyObjectsRequest, projectDataFieldsRequest, boundsRequest]);

    return { map, localObjects, projectDataFields, mapObjectBounds };
};

export const getLocalOnlyGeoJson = async (
    mappingApi: AxiosInstance,
    mapId: string,
    additionalFilters: FilterFormat[],
    getWaypoints: (objectId: string, mapId: string) => Promise<RouteWaypoint[]>,
    signal?: AbortSignal
) => {
    // Request for local only map objects (e.g. 3D models)
    const filter = [{ fieldName: "StyleType", operator: IventisFilterOperator.In, value: [StyleType.Model, StyleType.LineModel] }, ...additionalFilters];
    const objects = await getLocalGeoJson(mappingApi, mapId, getWaypoints, filter, signal);
    return objects;
};

export const getLocalGeoJson = async (
    mappingApi: AxiosInstance,
    mapId: string,
    getWaypoints: (objectId: string, mapId: string) => Promise<RouteWaypoint[]>,
    filter: FilterFormat[],
    signal?: AbortSignal
) => {
    const request = {
        params: {
            filter: JSON.stringify(filter),
            mapId,
        },
        paramsSerializer: (params) => QueryString.stringify(params, { arrayFormat: "repeat" }),
    };
    const objects = await aggregatePagedResponse<MapObject[], LocalGeoJson>(mappingApi, `/maps/${mapId}/map_objects/filter`, { ...request, signal }, async (cum, objects) => {
        const waypoints = await Promise.all(
            objects.map<Promise<RouteWaypoint[]>>(async (object) => {
                let waypoints: RouteWaypoint[] | null = null;
                if ((object.modeOfTransport ?? object.geoJsonFeature?.properties?.modeOfTransport) != null) {
                    waypoints = await getWaypoints(object.id, mapId);
                }
                return waypoints;
            })
        );
        return objects
            .map((object, index) => convertMapObjectToLocalGeoJson(object, waypoints[index]))
            .reduce((acc, object) => {
                const { layerid } = object.feature.properties;
                const existingObjects = acc[layerid];
                acc[layerid] = [...(existingObjects ?? []), object];
                return acc;
            }, cum ?? {});
    });
    return objects;
};

/**
 * Gets multiple models by their ids, only one request will be made for each unique id
 *
 * Has to be here due to a circular dependency redux issue
 */
export const multipleModelsGetter = async (ids: string[], mappingApi: AxiosInstance, signal?: AbortSignal) => {
    const models = await mappingApi.get<Model[]>(`models/lods`, {
        // Get all the unique ids so we aren't requesting the same model multiple times
        params: { ids: ids.reduce((acc, id) => (acc.includes(id) ? acc : [...acc, id]), []) },
        paramsSerializer: (params) => QueryString.stringify(params, { arrayFormat: "repeat" }),
        signal,
    });
    return models.data;
};

// Get the bounding box of map objects on a map
// Always includes non-visible layers when getting the bounds for all map objects as otherwise when the user makes the layers visible, the map will not think there are objects on the map tiles
export async function getMapObjectBounds(mappingApi: AxiosInstance, mapId: string, level: string, signal?: AbortSignal) {
    return mappingApi.get<Polygon>(`/maps/${mapId}/bounding-box?level=${level ?? 0}&includeHiddenLayers=true`, { signal });
}

export async function getCommentBounds(mappingApi: AxiosInstance, mapId: string, level: string, signal?: AbortSignal) {
    return mappingApi.get<Polygon>(`/maps/${mapId}/comments/bounding-box?level=${level ?? 0}`, { signal });
}

export async function getMapObjectAndCommentBounds(mappingApi: AxiosInstance, mapId: string, level: string, signal?: AbortSignal) {
    const mapObjectBoundsRequest = getMapObjectBounds(mappingApi, mapId, level, signal);

    const commentBoundsRequest = getCommentBounds(mappingApi, mapId, level, signal);

    let [{ data: mapObjectBounds }, { data: commentBounds }] = await Promise.all([mapObjectBoundsRequest, commentBoundsRequest]);

    // If the map object bounds is an empty string, set it to undefined
    // This is because the API returns an empty string when there are no map objects
    if (typeof mapObjectBounds === "string") {
        mapObjectBounds = undefined;
    }
    if (typeof commentBounds === "string") {
        commentBounds = undefined;
    }

    return { mapObjectBounds, commentBounds };
}
